import React, { useState } from 'react';
import styled from 'styled-components';
import {
  BaseProps,
  FlexRow,
  SvgIcon,
  useClickAway,
  useMatchMedia,
} from '@make-software/cspr-ui';

type Direction = 'row' | 'column';

export interface NavDropdownSettings {
  gap?: number;
  itemPadding?: string;
  containerPadding?: string;
  dropdownPadding?: string;
  width?: number | 'auto';
  height?: number | 'auto';
  left?: number | string | 'auto';
  right?: number | string | 'auto';
}

type EventHandlers = {
  onClick?: (ev: any) => void;
  onMouseOver?: (ev: any) => void;
  onMouseLeave?: (ev: any) => void;
};

export interface NavDropdownProps extends BaseProps {
  opened?: boolean;
  customContainer?: React.ReactNode;
  itemList?: {
    handleClick?: () => void;
    component: React.ReactNode;
    selected?: boolean;
  }[];
  dropdownSettings?: NavDropdownSettings;
  title: React.ReactNode | string;
  hasScroll?: boolean;
  hideArrowButton?: boolean;
  multiColumnView?: boolean;
  direction?: Direction;
  spotlightTargetClassName?: string;
}

const NavDropdownContainer = styled.div<{
  left?: string | number | 'auto';
  right?: string | number | 'auto';
}>(({ theme, left, right }) => ({
  paddingTop: '20px',
  position: 'absolute',
  left: left ? left : 'auto',
  right: right ? right : 'auto',
  top: 6,
  zIndex: theme.zIndex.dropdown,
  //added to fix safari bug with z-index
  transform: 'translate3d(0, 0, 1rem)',
}));

const NavDropdownMenu = styled.ul<{
  gap?: number;
  width?: number | 'auto';
  height?: number | 'auto';
  itemPadding?: string;
  containerPadding?: string;
  dropdownPadding?: string;
  active?: boolean;
  hasScroll?: boolean;
  multiColumnView?: boolean;
  direction?: Direction;
}>(
  ({
    theme,
    gap,
    width,
    height,
    itemPadding,
    containerPadding,
    dropdownPadding,
    active,
    hasScroll,
    direction = 'row',
    multiColumnView,
  }) => ({
    display: 'flex',
    flexDirection: direction,
    gap: gap,
    width: width ?? 'fit-content',
    height: height ?? 'auto',
    background: theme.styleguideColors.backgroundPrimary,
    filter: theme.boxShadow.dropdown,
    padding: dropdownPadding ?? '16px 0',
    borderTop: `5px solid ${theme.styleguideColors.fillPrimaryRed}`,
    borderRadius: theme.borderRadius.base,

    [`${NavDropdownItem} > *`]: {
      padding: itemPadding ?? '10px 0 10px 16px',
    },

    ...(multiColumnView && {
      padding: containerPadding ?? '8px',
      flexWrap: 'wrap',

      [`${NavDropdownItem}`]: {
        '& > *': {
          padding: itemPadding ?? '8px',
        },
        '&:hover': {
          borderRadius: theme.borderRadius.base,
          background: theme.styleguideColors.fillSecondary,
        },
      },
    }),

    ...(hasScroll && {
      overflow: 'auto',
      padding: containerPadding ?? '8px 0',
    }),
  })
);

const NavItem = styled.li(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  height: 'fit-content',
}));

export const NavDropdownItem = styled(NavItem)<{ selected?: boolean }>(
  ({ selected, theme }) => ({
    display: 'flex',
    cursor: 'pointer',

    '> a': {
      color: theme.styleguideColors.contentPrimary,
    },

    '&:hover': {
      background: theme.styleguideColors.fillSecondary,
    },

    ...(selected && {
      background: theme.styleguideColors.fillSecondary,
      pointerEvents: 'none',
    }),
  })
);

const ArrowSvgIcon = styled(SvgIcon)<{ isOpen: boolean }>(
  ({ theme, isOpen }) => ({
    path: {
      fill: isOpen
        ? theme.styleguideColors.contentOnFill
        : theme.styleguideColors.contentTertiary,
    },
  })
);

export const NavDropdownInner = React.forwardRef<
  HTMLDivElement,
  NavDropdownProps & { isOpen: boolean }
>(
  (
    {
      customContainer = null,
      itemList,
      title,
      dropdownSettings,
      hasScroll,
      multiColumnView,
      direction = 'column',
      hideArrowButton = false,
      isOpen,
    }: NavDropdownProps & { isOpen: boolean },
    ref
  ) => (
    <>
      <FlexRow ref={ref} align="center">
        {title}
        {!hideArrowButton && (
          <ArrowSvgIcon
            src="assets/icons/ic-arrow-down.svg"
            style={{ marginLeft: 8 }}
            isOpen={isOpen}
            rotate={isOpen}
          />
        )}
      </FlexRow>

      {isOpen && (
        <NavDropdownContainer
          left={dropdownSettings?.left}
          right={dropdownSettings?.right}
        >
          <NavDropdownMenu
            gap={dropdownSettings?.gap}
            width={dropdownSettings?.width}
            height={dropdownSettings?.height}
            itemPadding={dropdownSettings?.itemPadding}
            dropdownPadding={dropdownSettings?.dropdownPadding}
            containerPadding={dropdownSettings?.containerPadding}
            hasScroll={hasScroll}
            multiColumnView={multiColumnView}
            direction={direction}
          >
            {itemList
              ? itemList
                  .filter(({ component }) => component != null)
                  .map(({ handleClick, component, selected }, index) => (
                    <NavDropdownItem
                      key={index}
                      selected={selected}
                      onClick={handleClick && handleClick}
                    >
                      {component}
                    </NavDropdownItem>
                  ))
              : customContainer}
          </NavDropdownMenu>
        </NavDropdownContainer>
      )}
    </>
  )
);

export function NavDropdown(props: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const screenType = useMatchMedia(['mobile', 'mobile', 'desktop'], []);

  const { ref } = useClickAway({
    callback: () => {
      setIsOpen(false);
    },
  });

  const handleMouseOver = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return screenType === 'mobile' ? (
    <div onClick={handleClick}>
      <NavDropdownInner ref={ref} isOpen={isOpen} {...props} />
    </div>
  ) : (
    <div
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      className={props.spotlightTargetClassName}
    >
      <NavDropdownInner ref={ref} isOpen={isOpen} {...props} />
    </div>
  );
}

export default NavDropdown;
